/**
 * Copyright (C) 2021 Prosperia Salud, S.A. DE C.V. info@prosperia.health.
 * All rights reserved
 * DO NOT REDISTRIBUTE
 * The software MAY NOT be rewritten or refactored by any means. The code MAY
 * NOT be packaged and redistributed by any means without prior written
 * permission from the author.
 */
import React, {
  useEffect,
  lazy,
  useContext,
} from 'react';
/// /// EXTERNAL /// ///
// third party libraries //
import {
  Routes, Route, useNavigate, Navigate,
} from 'react-router-dom';
import TagManager from 'react-gtm-module';
/// /// INTERNAL /// ///
// components //
import EnhancedSuspense from 'components/atoms/common/EnhancedSuspense';
// context //
import { ErrorContext } from 'context/ErrorContext';
// utils //
import { isProduction } from 'utils/helper';
import { ErrorStatus } from 'utils/constants';
// components lazy //
const CommonProvider = lazy(() => import('components/providers/CommonProvider'));
const RetiniaProvider = lazy(() => import('components/providers/RetiniaProvider'));
const Retinia = lazy(() => import('components/pages/Retinia'));
const Error = lazy(() => import('components/pages/Error'));
const ChannelsPocs = lazy(() => import('components/templates/ChannelsPocs'));
const Evaluation = lazy(() => import('components/organisms/Evaluation'));
const Questions = lazy(() => import('components/organisms/Questions'));
const Findings = lazy(() => import('components/organisms/Findings'));
const PostInteraction = lazy(() => import('components/organisms/PostInteraction'));
const ProcessedPatients = lazy(() => import('components/molecules/processed/ProcessedPatients'));
const Patients = lazy(() => import('components/templates/Patients'));
const PatientDetails = lazy(() => import('components/molecules/patientDetails/PatientDetails'));
const EnhancedPatientForm = lazy(() => import('components/molecules/form/EnhancedPatientForm'));
const UploadingImages = lazy(() => import('components/molecules/model/UploadingImages'));
const Diseases = lazy(() => import('components/molecules/model/Diseases'));
const TermsPrivacy = lazy(() => import('components/atoms/common/TermsPrivacy'));
const AuthGuard = lazy(() => import('components/guard/AuthGuard'));
const RetiniaGuard = lazy(() => import('components/guard/RetiniaGuard'));

const App = () => {
  const {
    setMessage,
  } = useContext(ErrorContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (isProduction) {
      const GTM_ID = {
        gtmId: 'GTM-N7C6D27',
      };
      TagManager.initialize(GTM_ID);
    }
    const updated = localStorage.getItem('updated');
    if (updated) {
      setMessage({
        open: true,
        severity: 'success',
        duration: 5000,
        text: 'La aplicación se ha actualizado correctamente.',
      });
      localStorage.removeItem('updated');
    }
  }, [setMessage]);

  const handleTermsPrivacy = () => {
    navigate(-1);
  };

  return (
    <Routes>
      <Route
        element={(
          <EnhancedSuspense>
            <AuthGuard>
              <CommonProvider />
            </AuthGuard>
          </EnhancedSuspense>
        )}
      >
        <Route element={<Retinia />}>
          <Route index path="/channels" element={<ChannelsPocs />} />
          <Route index path="/processed_patients" element={<ProcessedPatients />} />
        </Route>

        <Route path="/:channelID/:POC" element={<RetiniaProvider />}>
          <Route index element={<Patients />} />
          <Route path="patient/:interactionId" element={<PatientDetails />} />
          <Route path="form" element={<EnhancedPatientForm />} />
          <Route
            path="terms_privacy"
            element={(
              <TermsPrivacy
                handleBack={handleTermsPrivacy}
                termsLogo="https://media-static.s3.amazonaws.com/image/terms_logo.png"
                privacyLogo="https://media-static.s3.amazonaws.com/image/privacy_logo.png"
              />
            )}
          />
          <Route element={<Evaluation />}>
            <Route
              path="questionnaire"
              element={(
                <RetiniaGuard>
                  <Questions />
                </RetiniaGuard>
              )}
            />
            <Route
              path="findings"
              element={(
                <RetiniaGuard>
                  <Findings />
                </RetiniaGuard>
              )}
            />
            <Route
              path="processing"
              element={(
                <RetiniaGuard>
                  <UploadingImages />
                </RetiniaGuard>
              )}
            />
            <Route
              path="diseases"
              element={(
                <RetiniaGuard>
                  <Diseases />
                </RetiniaGuard>
              )}
            />
          </Route>
          <Route
            path="interaction"
            element={(
              <RetiniaGuard>
                <PostInteraction />
              </RetiniaGuard>
            )}
          />
        </Route>

        <Route path="/error/:status" element={<Error />} />
        <Route path="*" element={<EnhancedSuspense><Error statusProps={ErrorStatus.NOT_FOUND} /></EnhancedSuspense>} />

      </Route>
      <Route exact path="/" element={<Navigate to="/channels" />} />
    </Routes>
  );
};

export default App;
